import { Icon } from "@southfields-digital/mxmz-react-ui";
import classNames from "classnames";

import ResolutionMismatchWarning from "src/components/ResolutionMismatchWarning";

import type { CanvasOption } from "./types";

type CanvasOutputPickerOverlayProps = {
	graphic?: Graphic;
	options: CanvasOption[];
	selectedIds: Canvas["id"][];
	hasOverride: boolean;
	readOnly?: boolean;
	onToggle: (canvasId: Canvas["id"], checked: boolean) => void;
	onReset: () => void;
};

export default function CanvasOutputPickerOverlay({
	graphic,
	options,
	selectedIds,
	hasOverride,
	readOnly = false,
	onToggle,
	onReset,
}: CanvasOutputPickerOverlayProps) {
	return (
		<div
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<ul className="flex flex-col w-[150px] max-h-24 overflow-y-auto">
				{options.map((canvas) => {
					if (!canvas) return null;

					const isChecked = selectedIds.includes(canvas.id);
					const isLastSelected =
						!readOnly && selectedIds.length === 1 && selectedIds.includes(canvas.id);
					const isDisabled = readOnly || isLastSelected;

					return (
						<li
							key={canvas.id}
							className={classNames("transition-colors", !readOnly && "hover:bg-elevated-2")}
						>
							<label
								className={classNames(
									"flex items-center gap-1 px-3 text-xs font-medium select-none w-full h-6",
									readOnly ? "text-gray-500" : "text-gray-100 cursor-pointer",
								)}
								title={
									readOnly
										? "Disabled because the graphic is live"
										: isLastSelected
											? "At least one output must be selected"
											: undefined
								}
							>
								<input
									type="checkbox"
									className="sr-only"
									disabled={isDisabled}
									checked={isChecked}
									onChange={(e) => onToggle(canvas.id, e.target.checked)}
								/>
								<span className="flex-1 min-w-0 truncate">{canvas.name}</span>
								{graphic && isChecked && (
									<ResolutionMismatchWarning graphic={graphic} canvas={canvas} />
								)}
								{isChecked && (
									<Icon
										icon="check"
										weight="bold"
										size={12}
										className={classNames(
											"flex-shrink-0",
											readOnly ? "text-gray-400" : "text-white",
										)}
									/>
								)}
							</label>
						</li>
					);
				})}
			</ul>
			{/* Only show the reset button if not read-only and has an override */}
			{!readOnly && hasOverride && (
				<div className="px-3 pb-1 border-t border-white/10">
					<button
						className="text-xs text-gray-200 hover:text-gray-100 active:text-white transition-colors w-full text-left"
						onClick={onReset}
					>
						Reset
					</button>
				</div>
			)}
		</div>
	);
}
